import { defineExperiment } from '@learnforge/demo-render/harness';
import type {
  ExperimentSpec,
  PanelRow,
  ActionBarSpec,
  HarnessController,
} from '@learnforge/demo-render/harness';
import type {
  FigureElement,
  FigureHandle,
  MarksHandle,
  ReadoutHandle,
  ReadoutRow,
} from '@learnforge/demo-render';
import type { GeometryContext } from '../types';
import type { GeometryKey } from '../locales/keys';
import type { Vec2, Affine, TransformClass } from '@learnforge/demo-math/geometry';
import {
  add,
  sub,
  len,
  normalize,
  scale,
  cross,
  translation,
  rotation,
  reflection,
  dilation,
  compose,
  apply,
  classify,
  symmetryAxes,
  rotationOrder,
  regularPolygonVertices,
} from '@learnforge/demo-math/geometry';
import { TRANSFORM_PRESETS } from '../data/transform-presets';
import type { TransformMode, TransformPicker, TilePicker } from '../data/transform-presets';

// ============================================================================
// Pure Helper Functions (exported and tested)
// ============================================================================

/** Asymmetric flag-shaped polygon (no reflection symmetry in any axis) */
export const FLAG_VERTICES: readonly Vec2[] = [
  { x: 0, y: 0 }, // base left
  { x: 60, y: 0 }, // base right
  { x: 60, y: -10 }, // step
  { x: 80, y: -30 }, // tip right
  { x: 60, y: -50 }, // upper right
  { x: 20, y: -50 }, // upper left
  { x: 20, y: -20 }, // notch
  { x: 0, y: -20 }, // notch left
];

/** Snap an angle (degrees) to the nearest 15° increment */
export function snap15(deg: number): number {
  return Math.round(deg / 15) * 15;
}

// ---- Transform mode models ----

export interface TranslateModel {
  affine: Affine;
  vector: Vec2;
  preImage: Vec2[];
  image: Vec2[];
}

export function buildTranslateModel(
  flagVerts: readonly Vec2[],
  flagOrigin: Vec2,
  vector: Vec2,
): TranslateModel {
  const affine = translation(vector);
  const preImage = flagVerts.map((v) => add(v, flagOrigin));
  const image = preImage.map((v) => apply(affine, v));
  return { affine, vector, preImage, image };
}

export interface RotateModel {
  affine: Affine;
  centre: Vec2;
  angleDeg: number; // snapped to 15°
  preImage: Vec2[];
  image: Vec2[];
}

export function buildRotateModel(
  flagVerts: readonly Vec2[],
  flagOrigin: Vec2,
  centre: Vec2,
  rawAngleDeg: number,
): RotateModel {
  const angleDeg = snap15(rawAngleDeg);
  const affine = rotation(centre, angleDeg);
  const preImage = flagVerts.map((v) => add(v, flagOrigin));
  const image = preImage.map((v) => apply(affine, v));
  return { affine, centre, angleDeg, preImage, image };
}

export interface ReflectModel {
  affine: Affine;
  axisPoint: Vec2;
  axisDir: Vec2; // unit direction of mirror line
  preImage: Vec2[];
  image: Vec2[];
}

export function buildReflectModel(
  flagVerts: readonly Vec2[],
  flagOrigin: Vec2,
  axisPoint: Vec2,
  axisDir: Vec2,
): ReflectModel {
  const dir = len(axisDir) < 1e-9 ? { x: 1, y: 0 } : normalize(axisDir);
  const affine = reflection(axisPoint, dir);
  const preImage = flagVerts.map((v) => add(v, flagOrigin));
  const image = preImage.map((v) => apply(affine, v));
  return { affine, axisPoint, axisDir: dir, preImage, image };
}

export interface DilateModel {
  affine: Affine;
  centre: Vec2;
  k: number;
  preImage: Vec2[];
  image: Vec2[];
}

export function buildDilateModel(
  flagVerts: readonly Vec2[],
  flagOrigin: Vec2,
  centre: Vec2,
  rawK: number,
): DilateModel {
  // Clamp k: avoid 0 (degenerate)
  let k = Math.max(-4, Math.min(4, rawK));
  if (Math.abs(k) < 0.1) k = k < 0 ? -0.1 : 0.1;
  const affine = dilation(centre, k);
  const preImage = flagVerts.map((v) => add(v, flagOrigin));
  const image = preImage.map((v) => apply(affine, v));
  return { affine, centre, k, preImage, image };
}

// ---- Symmetry mode models ----

export interface SymmetryFigure {
  id: string;
  vertices: Vec2[];
}

export const SYMMETRY_FIGURES: readonly SymmetryFigure[] = [
  {
    id: 'square',
    vertices: regularPolygonVertices(4, 80, { x: 400, y: 230 }),
  },
  {
    id: 'pentagon',
    vertices: regularPolygonVertices(5, 80, { x: 400, y: 230 }),
  },
  {
    id: 'hexagon',
    vertices: regularPolygonVertices(6, 80, { x: 400, y: 230 }),
  },
  {
    id: 'equilateral-triangle',
    vertices: regularPolygonVertices(3, 80, { x: 400, y: 230 }),
  },
  {
    id: 'scalene',
    vertices: [
      { x: 300, y: 280 },
      { x: 460, y: 310 },
      { x: 350, y: 160 },
    ],
  },
];

export interface SymmetryModel {
  vertices: Vec2[];
  candidateAxes: { point: Vec2; dir: Vec2 }[];
  matchedAxisIndex: number | null; // index into candidateAxes
  /** True when matchedAxisIndex is non-null — candidateAxes are pre-filtered to true symmetry axes */
  mapsOntoItself: boolean;
  rotOrder: number;
}

export function buildSymmetryModel(
  vertices: Vec2[],
  matchedAxisIndex: number | null,
): SymmetryModel {
  const candidateAxes = symmetryAxes(vertices);
  const rotOrder = rotationOrder(vertices);
  // All candidateAxes are verified true symmetry axes (symmetryAxes filters them).
  // So mapsOntoItself is true whenever an axis is selected.
  const mapsOntoItself =
    matchedAxisIndex !== null && matchedAxisIndex >= 0 && matchedAxisIndex < candidateAxes.length;
  return { vertices, candidateAxes, matchedAxisIndex, mapsOntoItself, rotOrder };
}

// ---- Composition mode models ----

export interface CompositionStep {
  picker: TransformPicker;
  affine: Affine;
  label: string; // short description of the transform
}

export interface CompositionModel {
  steps: [CompositionStep, CompositionStep];
  composed: Affine;
  equivalent: TransformClass;
  equivalentLabel: string;
  preImage: Vec2[];
  intermediateImage: Vec2[];
  finalImage: Vec2[];
}

export function classifyLabel(tc: TransformClass): string {
  switch (tc.kind) {
    case 'identity':
      return 'Identity';
    case 'translation':
      return `Translation (${tc.vector.x.toFixed(1)}, ${tc.vector.y.toFixed(1)})`;
    case 'rotation':
      return `Rotation ${tc.deg.toFixed(1)}° about (${tc.center.x.toFixed(1)}, ${tc.center.y.toFixed(1)})`;
    case 'reflection':
      return `Reflection`;
    case 'glide':
      return `Glide reflection`;
    case 'dilation':
      return `Dilation k=${tc.k.toFixed(2)}`;
    case 'other':
      return 'Other';
  }
}

export function buildCompositionModel(
  flagVerts: readonly Vec2[],
  flagOrigin: Vec2,
  step1: CompositionStep,
  step2: CompositionStep,
  eps: number,
): CompositionModel {
  const preImage = flagVerts.map((v) => add(v, flagOrigin));
  const intermediateImage = preImage.map((v) => apply(step1.affine, v));
  const composed = compose(step2.affine, step1.affine);
  const finalImage = preImage.map((v) => apply(composed, v));
  const equivalent = classify(composed, eps);
  return {
    steps: [step1, step2],
    composed,
    equivalent,
    equivalentLabel: classifyLabel(equivalent),
    preImage,
    intermediateImage,
    finalImage,
  };
}

// ---- Tessellation mode models ----

export interface TilePlacement {
  vertices: Vec2[];
  affine: Affine;
  transformClass: TransformClass;
  label: string;
}

export interface TessellationModel {
  tileVertices: Vec2[]; // canonical tile (centred at tileOrigin)
  tileOrigin: Vec2;
  placements: TilePlacement[]; // does NOT include the identity tile
}

/** Generate the canonical vertices for a tile type, centred at origin */
function tileShape(tile: TilePicker, size: number): Vec2[] {
  switch (tile) {
    case 'square':
      return [
        { x: -size / 2, y: -size / 2 },
        { x: size / 2, y: -size / 2 },
        { x: size / 2, y: size / 2 },
        { x: -size / 2, y: size / 2 },
      ];
    case 'hexagon':
      return regularPolygonVertices(6, size, { x: 0, y: 0 });
    case 'triangle':
      return regularPolygonVertices(3, size, { x: 0, y: 0 });
    case 'escher-square': {
      // Modified square with bumped-out top and bottom edges
      const h = size / 2;
      const bump = size * 0.2;
      return [
        { x: -h, y: -h },
        { x: 0, y: -h - bump },
        { x: h, y: -h },
        { x: h + bump, y: 0 },
        { x: h, y: h },
        { x: 0, y: h + bump },
        { x: -h, y: h },
        { x: -h - bump, y: 0 },
      ];
    }
  }
}

/** Generate translation vectors for one ring of neighbours */
function ringTranslations(tile: TilePicker, size: number): Vec2[] {
  switch (tile) {
    case 'square':
    case 'escher-square': {
      // 8 neighbours of the square grid
      const offsets: Vec2[] = [];
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue;
          offsets.push({ x: dx * size, y: dy * size });
        }
      }
      return offsets;
    }
    case 'hexagon': {
      // 6 immediate neighbours of a flat-top hexagon
      const w = Math.sqrt(3) * size;
      const h = 2 * size;
      return [
        { x: w, y: 0 },
        { x: -w, y: 0 },
        { x: w / 2, y: (3 * h) / 4 },
        { x: -w / 2, y: (3 * h) / 4 },
        { x: w / 2, y: -(3 * h) / 4 },
        { x: -w / 2, y: -(3 * h) / 4 },
      ];
    }
    case 'triangle': {
      // Equilateral triangle tiling: 3 translations + 3 reflections for a ring of 6
      // Use simpler: generate 6 translation vectors for 2-ring tight packing
      const r = size; // circumradius
      const h = (r * Math.sqrt(3)) / 2;
      return [
        { x: 2 * h, y: 0 },
        { x: -2 * h, y: 0 },
        { x: h, y: (3 * r) / 2 },
        { x: -h, y: (3 * r) / 2 },
        { x: h, y: -(3 * r) / 2 },
        { x: -h, y: -(3 * r) / 2 },
      ];
    }
  }
}

export function buildTessellationModel(
  tile: TilePicker,
  tileOrigin: Vec2,
  rings: number,
  eps: number,
): TessellationModel {
  const size = 60;
  const canonicalVerts = tileShape(tile, size);
  // Canonical tile placed at tileOrigin
  const tileVertices = canonicalVerts.map((v) => add(v, tileOrigin));
  const vectors = ringTranslations(tile, size);

  // For rings > 1, include multi-step translations
  const allVectors: Vec2[] = [];
  if (rings === 1) {
    allVectors.push(...vectors);
  } else {
    // Collect unique vectors up to `rings` steps
    const seen = new Set<string>();
    const queue: Vec2[] = [{ x: 0, y: 0 }];
    for (let r = 0; r < rings; r++) {
      const next: Vec2[] = [];
      for (const q of queue) {
        for (const v of vectors) {
          const nv: Vec2 = { x: q.x + v.x, y: q.y + v.y };
          const key = `${Math.round(nv.x)},${Math.round(nv.y)}`;
          if (!seen.has(key) && !(Math.abs(nv.x) < 1 && Math.abs(nv.y) < 1)) {
            seen.add(key);
            allVectors.push(nv);
            next.push(nv);
          }
        }
      }
      queue.length = 0;
      queue.push(...next);
    }
  }

  const placements: TilePlacement[] = allVectors.map((vec) => {
    const affine = translation(vec);
    const vertices = tileVertices.map((v) => apply(affine, v));
    const tc = classify(affine, eps);
    return { vertices, affine, transformClass: tc, label: classifyLabel(tc) };
  });

  return { tileVertices, tileOrigin, placements };
}

// ---- Vertex disjointness check (for tessellation tests) ----

/** Check whether two placement vertex-sets are disjoint (no vertex within tolerance) */
export function placementsDisjoint(a: TilePlacement, b: TilePlacement, tol = 1): boolean {
  for (const va of a.vertices) {
    for (const vb of b.vertices) {
      const dx = va.x - vb.x;
      const dy = va.y - vb.y;
      if (Math.sqrt(dx * dx + dy * dy) < tol) return false;
    }
  }
  return true;
}

// ============================================================================
// Stage constants
// ============================================================================

const FLAG_ORIGIN: Vec2 = { x: 200, y: 290 };
const TESS_ORIGIN: Vec2 = { x: 400, y: 230 };

// ============================================================================
// State interface
// ============================================================================

interface TransformState {
  mode: TransformMode;
  picker: TransformPicker;
  // transform-mode params
  translateVec: Vec2;
  rotateCentre: Vec2;
  rotateAngleDeg: number;
  reflectAxisPoint: Vec2;
  reflectAxisAngleDeg: number;
  dilateCentre: Vec2;
  dilateK: number;
  // symmetry-mode
  symFigureId: string;
  symMirrorA: Vec2;
  symMirrorB: Vec2;
  symFoundAxes: number[];
  // composition-mode
  compFirstPicker: TransformPicker;
  compSecondPicker: TransformPicker;
  comp1AxisPoint: Vec2;
  comp1AxisAngleDeg: number;
  comp2AxisPoint: Vec2;
  comp2AxisAngleDeg: number;
  // tessellation-mode
  tessTile: TilePicker;
}

// ============================================================================
// Pure helpers for state
// ============================================================================

function symFigCentroidFor(symFigureId: string): Vec2 {
  const fig = SYMMETRY_FIGURES.find((f) => f.id === symFigureId) ?? SYMMETRY_FIGURES[0]!;
  const verts = fig.vertices;
  let sx = 0;
  let sy = 0;
  for (const v of verts) {
    sx += v.x;
    sy += v.y;
  }
  return { x: sx / verts.length, y: sy / verts.length };
}

function initSymMirrorState(symFigureId: string): {
  symMirrorA: Vec2;
  symMirrorB: Vec2;
  symFoundAxes: number[];
} {
  const c = symFigCentroidFor(symFigureId);
  return {
    symMirrorA: { x: c.x, y: c.y - 110 },
    symMirrorB: { x: c.x, y: c.y + 110 },
    symFoundAxes: [],
  };
}

function defaultState(): TransformState {
  const symMirror = initSymMirrorState('square');
  return {
    mode: 'transform',
    picker: 'rotate',
    translateVec: { x: 150, y: -80 },
    rotateCentre: { x: 400, y: 230 },
    rotateAngleDeg: 90,
    reflectAxisPoint: { x: 400, y: 230 },
    reflectAxisAngleDeg: 90,
    dilateCentre: { x: 400, y: 230 },
    dilateK: 1.5,
    symFigureId: 'square',
    symMirrorA: symMirror.symMirrorA,
    symMirrorB: symMirror.symMirrorB,
    symFoundAxes: symMirror.symFoundAxes,
    compFirstPicker: 'reflect',
    compSecondPicker: 'reflect',
    comp1AxisPoint: { x: 350, y: 230 },
    comp1AxisAngleDeg: 90,
    comp2AxisPoint: { x: 450, y: 230 },
    comp2AxisAngleDeg: 90,
    tessTile: 'square',
  };
}

const ADVANCED_MODES: readonly TransformMode[] = ['composition', 'tessellation'];

function effectiveModeFor(state: TransformState, advanced: boolean): TransformMode {
  return !advanced && ADVANCED_MODES.includes(state.mode) ? 'transform' : state.mode;
}

function effectivePickerFor(state: TransformState, advanced: boolean): TransformPicker {
  return !advanced && state.picker === 'dilate' ? 'rotate' : state.picker;
}

function matchSymmetryAxis(verts: Vec2[], mirrorA: Vec2, mirrorB: Vec2): number | null {
  const PARALLEL_EPS = 0.03;
  const DIST_EPS = 6;

  const mirrorDir = sub(mirrorB, mirrorA);
  const mirrorDirUnit = len(mirrorDir) < 1e-9 ? { x: 0, y: 1 } : normalize(mirrorDir);

  const trueAxes = symmetryAxes(verts);

  for (let i = 0; i < trueAxes.length; i++) {
    const ax = trueAxes[i]!;
    const axisDirUnit = normalize(ax.dir);
    const parallelness = Math.abs(cross(mirrorDirUnit, axisDirUnit));
    const toAxisPt = sub(ax.point, mirrorA);
    const perpDist = Math.abs(cross(mirrorDirUnit, toAxisPt));
    if (parallelness < PARALLEL_EPS && perpDist < DIST_EPS) {
      return i;
    }
  }
  return null;
}

function snapMirrorToAxis(
  verts: Vec2[],
  axisIdx: number,
): {
  symMirrorA: Vec2;
  symMirrorB: Vec2;
} {
  const trueAxes = symmetryAxes(verts);
  const snapAx = trueAxes[axisIdx]!;
  const snapDir = normalize(snapAx.dir);
  return {
    symMirrorA: sub(snapAx.point, scale(snapDir, 110)),
    symMirrorB: add(snapAx.point, scale(snapDir, 110)),
  };
}

// ============================================================================
// Harness implementation
// ============================================================================

function buildSpec(): ExperimentSpec<TransformState, GeometryKey, GeometryContext> {
  let controllerRef: HarnessController<TransformState>;
  let figure: FigureHandle;
  let marks: MarksHandle;
  let readout: ReadoutHandle;

  let symFlashTimeout: ReturnType<typeof setTimeout> | null = null;
  const symSpin = { active: false, rafId: null as number | null, t: 0, figureId: '' };

  function triggerSymmetryFlash(): void {
    if (symFlashTimeout !== null) {
      clearTimeout(symFlashTimeout);
      symFlashTimeout = null;
      controllerRef.svg.classList.remove('geo-symmetry-flash');
    }
    controllerRef.svg.classList.add('geo-symmetry-flash');
    symFlashTimeout = setTimeout(() => {
      controllerRef.svg.classList.remove('geo-symmetry-flash');
      symFlashTimeout = null;
    }, 400);
  }

  function stopSymSpin(): void {
    if (symSpin.rafId !== null) {
      controllerRef.cancelRaf(symSpin.rafId);
      symSpin.rafId = null;
    }
    symSpin.active = false;
  }

  function startSymSpin(state: TransformState): void {
    stopSymSpin();

    const DURATION_MS = 900;
    const startTime = performance.now();

    symSpin.active = true;
    symSpin.figureId = state.symFigureId;

    const frame = (now: number): void => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / DURATION_MS, 1);
      symSpin.t = t;
      controllerRef.requestRender();

      if (t < 1) {
        symSpin.rafId = controllerRef.raf(frame);
      } else {
        symSpin.active = false;
        symSpin.rafId = null;
        controllerRef.requestRender();
      }
    };

    symSpin.rafId = controllerRef.raf(frame);
  }

  function buildFlagElems(
    prefix: string,
    verts: Vec2[],
    draggable: boolean,
    classPrefix: string,
  ): FigureElement[] {
    const elems: FigureElement[] = [];
    for (let i = 0; i < verts.length; i++) {
      elems.push({
        kind: 'point',
        id: `${prefix}-pt-${i}`,
        at: verts[i]!,
        ...(draggable ? { draggable: {} } : {}),
        style: { className: `${classPrefix}-pt` },
      });
    }
    elems.push({
      kind: 'polygon',
      id: `${prefix}-poly`,
      vertices: verts.map((_, i) => `${prefix}-pt-${i}`),
      style: { className: `${classPrefix}-poly` },
    });
    return elems;
  }

  return {
    labelKey: 'experiment.transformations',
    demoId: '2d-geometry',
    advancedKey: 'transformations',

    initialState() {
      return defaultState();
    },

    applyPreset(state, presetId, ctx) {
      return applyPresetLocal(state, presetId, ctx.advanced);
    },

    setup(_ctx, controller) {
      controllerRef = controller;
      figure = controller.createFigure();
      marks = controller.createMarks();
      readout = controller.createReadout();
      controller.registerCleanup(() => {
        if (symFlashTimeout !== null) {
          clearTimeout(symFlashTimeout);
          symFlashTimeout = null;
        }
        stopSymSpin();
      });
    },

    render(state: TransformState, ctx) {
      const eMode = effectiveModeFor(state, ctx.advanced);
      const ePicker = effectivePickerFor(state, ctx.advanced);

      if (symSpin.active && eMode === 'symmetry' && symSpin.figureId === state.symFigureId) {
        // Render spinning symmetry figure
        const fig =
          SYMMETRY_FIGURES.find((f) => f.id === state.symFigureId) ?? SYMMETRY_FIGURES[0]!;
        const verts = fig.vertices;

        let cx = 0;
        let cy = 0;
        for (const v of verts) {
          cx += v.x;
          cy += v.y;
        }
        const centroid: Vec2 = { x: cx / verts.length, y: cy / verts.length };

        const t = symSpin.t;
        const angleDeg = 360 * (1 - (1 - t) * (1 - t));
        const aff = rotation(centroid, angleDeg);
        const spinVerts = verts.map((v) => apply(aff, v));

        const elems: FigureElement[] = [];
        for (let i = 0; i < spinVerts.length; i++) {
          elems.push({
            kind: 'point',
            id: `sym-spin-pt-${i}`,
            at: spinVerts[i]!,
            style: { className: 'geo-preimage-pt' },
          });
        }
        elems.push({
          kind: 'polygon',
          id: 'sym-spin-poly',
          vertices: spinVerts.map((_, i) => `sym-spin-pt-${i}`),
          style: { className: 'geo-preimage-poly' },
        });
        figure.update(elems);
        return;
      }

      switch (eMode) {
        case 'transform': {
          const elems: FigureElement[] = [];
          const rows: ReadoutRow[] = [];

          if (ePicker === 'translate') {
            const model = buildTranslateModel(FLAG_VERTICES, FLAG_ORIGIN, state.translateVec);
            elems.push(...buildFlagElems('pre', model.preImage, false, 'geo-preimage'));
            elems.push(...buildFlagElems('img', model.image, false, 'geo-image'));
            const arrowTip: Vec2 = add(FLAG_ORIGIN, state.translateVec);
            elems.push({
              kind: 'point',
              id: 'tx-flag-origin',
              at: FLAG_ORIGIN,
              style: { className: 'geo-dilation-centre' },
            });
            elems.push({
              kind: 'point',
              id: 'tx-arrow-tip',
              at: arrowTip,
              draggable: {},
              style: { className: 'geo-image-pt' },
            });
            elems.push({
              kind: 'segment',
              id: 'tx-arrow-seg',
              from: 'tx-flag-origin',
              to: 'tx-arrow-tip',
              style: { className: 'geo-ray-seg', dashed: true },
            });
            rows.push({
              id: 'tx-vector',
              lhs: 'v',
              relation: '=',
              rhs: `(${model.vector.x.toFixed(0)}, ${model.vector.y.toFixed(0)})`,
              emphasis: 'invariant',
            });
          } else if (ePicker === 'rotate') {
            const model = buildRotateModel(
              FLAG_VERTICES,
              FLAG_ORIGIN,
              state.rotateCentre,
              state.rotateAngleDeg,
            );
            elems.push(...buildFlagElems('pre', model.preImage, false, 'geo-preimage'));
            elems.push(...buildFlagElems('img', model.image, false, 'geo-image'));
            elems.push({
              kind: 'point',
              id: 'rot-centre',
              at: model.centre,
              draggable: {},
              style: { className: 'geo-dilation-centre' },
            });
            const handleAngleRad = (model.angleDeg * Math.PI) / 180;
            const angleHandlePos: Vec2 = add(model.centre, {
              x: 60 * Math.cos(handleAngleRad),
              y: 60 * Math.sin(handleAngleRad),
            });
            elems.push({
              kind: 'point',
              id: 'rot-angle-handle',
              at: angleHandlePos,
              draggable: {},
              style: { className: 'geo-intercept-pt' },
            });
            elems.push({
              kind: 'segment',
              id: 'rot-handle-seg',
              from: 'rot-centre',
              to: 'rot-angle-handle',
              style: { className: 'geo-ray-seg', dashed: true },
            });
            rows.push({
              id: 'rot-angle',
              lhs: 'θ',
              relation: '=',
              rhs: `${model.angleDeg}°`,
              emphasis: 'invariant',
            });
            rows.push({
              id: 'rot-centre-readout',
              lhs: 'centre',
              relation: '=',
              rhs: `(${Math.round(model.centre.x)}, ${Math.round(model.centre.y)})`,
              emphasis: 'neutral',
            });
          } else if (ePicker === 'reflect') {
            const axisRad = (state.reflectAxisAngleDeg * Math.PI) / 180;
            const axisDir: Vec2 = { x: Math.cos(axisRad), y: Math.sin(axisRad) };
            const model = buildReflectModel(
              FLAG_VERTICES,
              FLAG_ORIGIN,
              state.reflectAxisPoint,
              axisDir,
            );
            elems.push(...buildFlagElems('pre', model.preImage, false, 'geo-preimage'));
            elems.push(...buildFlagElems('img', model.image, false, 'geo-image'));
            const axisLen = 120;
            const dir = model.axisDir;
            const p1: Vec2 = sub(model.axisPoint, scale(dir, axisLen));
            const p2: Vec2 = add(model.axisPoint, scale(dir, axisLen));
            elems.push({
              kind: 'point',
              id: 'ref-axis-p1',
              at: p1,
              draggable: {},
              style: { className: 'geo-dilation-centre' },
            });
            elems.push({
              kind: 'point',
              id: 'ref-axis-p2',
              at: p2,
              draggable: {},
              style: { className: 'geo-dilation-centre' },
            });
            elems.push({
              kind: 'segment',
              id: 'ref-axis-line',
              from: 'ref-axis-p1',
              to: 'ref-axis-p2',
              style: { className: 'geo-intercept-seg' },
            });
            rows.push({
              id: 'ref-axis',
              lhs: 'mirror',
              relation: '=',
              rhs: `${Math.round(state.reflectAxisAngleDeg)}°`,
              emphasis: 'invariant',
            });
          } else if (ePicker === 'dilate') {
            const model = buildDilateModel(
              FLAG_VERTICES,
              FLAG_ORIGIN,
              state.dilateCentre,
              state.dilateK,
            );
            elems.push(...buildFlagElems('pre', model.preImage, false, 'geo-preimage'));
            elems.push(...buildFlagElems('img', model.image, false, 'geo-image'));
            elems.push({
              kind: 'point',
              id: 'dil-centre',
              at: model.centre,
              draggable: {},
              style: { className: 'geo-dilation-centre' },
            });
            rows.push({
              id: 'dil-k',
              lhs: 'k',
              relation: '=',
              rhs: model.k.toFixed(2),
              emphasis: 'invariant',
            });
          }

          figure.update(elems);
          marks.update([]);
          readout.update(rows);
          break;
        }

        case 'symmetry': {
          const fig =
            SYMMETRY_FIGURES.find((f) => f.id === state.symFigureId) ?? SYMMETRY_FIGURES[0]!;
          const verts = fig.vertices;

          const mirrorDir = sub(state.symMirrorB, state.symMirrorA);
          const mirrorDirUnit = len(mirrorDir) < 1e-9 ? { x: 0, y: 1 } : normalize(mirrorDir);

          const trueAxes = symmetryAxes(verts);
          const rotOrder = rotationOrder(verts);

          const currentMatchedAxisIndex = matchSymmetryAxis(
            verts,
            state.symMirrorA,
            state.symMirrorB,
          );
          const isOnAxis = currentMatchedAxisIndex !== null;

          const mirrorAffine = reflection(state.symMirrorA, mirrorDirUnit);
          const imageVerts = verts.map((v) => apply(mirrorAffine, v));

          const elems: FigureElement[] = [];

          for (let i = 0; i < verts.length; i++) {
            elems.push({
              kind: 'point',
              id: `sym-pt-${i}`,
              at: verts[i]!,
              style: { className: 'geo-preimage-pt' },
            });
          }
          elems.push({
            kind: 'polygon',
            id: 'sym-poly',
            vertices: verts.map((_, i) => `sym-pt-${i}`),
            style: { className: 'geo-preimage-poly' },
          });

          const imagePolyClass = isOnAxis ? 'geo-image-poly geo-glow' : 'geo-image-poly';
          for (let i = 0; i < imageVerts.length; i++) {
            elems.push({
              kind: 'point',
              id: `sym-img-pt-${i}`,
              at: imageVerts[i]!,
              style: { className: isOnAxis ? 'geo-image-pt geo-glow' : 'geo-image-pt' },
            });
          }
          elems.push({
            kind: 'polygon',
            id: 'sym-img-poly',
            vertices: imageVerts.map((_, i) => `sym-img-pt-${i}`),
            style: { className: imagePolyClass },
          });

          elems.push({
            kind: 'point',
            id: 'mirror-a',
            at: state.symMirrorA,
            draggable: {},
            style: { className: 'geo-dilation-centre' },
          });
          elems.push({
            kind: 'point',
            id: 'mirror-b',
            at: state.symMirrorB,
            draggable: {},
            style: { className: 'geo-dilation-centre' },
          });
          elems.push({
            kind: 'line',
            id: 'mirror-line',
            through: ['mirror-a', 'mirror-b'],
            style: { className: 'geo-intercept-seg', dashed: true },
          });

          figure.update(elems);
          marks.update([]);

          const allFound = trueAxes.length > 0 && state.symFoundAxes.length === trueAxes.length;
          const rows: ReadoutRow[] = [
            {
              id: 'sym-drag-hint',
              lhs: ctx.i18n.t('transform.symmetry.drag-hint'),
              relation: '=',
              rhs: '',
              emphasis: 'neutral',
            },
            {
              id: 'sym-axes-count',
              lhs: ctx.i18n.t('transform.symmetry.axes'),
              relation: '=',
              rhs: trueAxes.length > 0 ? `${state.symFoundAxes.length} / ${trueAxes.length}` : '0',
              emphasis: allFound ? 'invariant' : 'neutral',
            },
            {
              id: 'sym-rot-order',
              lhs: ctx.i18n.t('transform.symmetry.rotation-order'),
              relation: '=',
              rhs: String(rotOrder),
              emphasis: 'invariant',
            },
          ];
          if (isOnAxis) {
            rows.push({
              id: 'sym-axis-found',
              lhs: ctx.i18n.t('transform.symmetry.axis-found'),
              relation: '=',
              rhs: '',
              emphasis: 'invariant',
            });
          }
          readout.update(rows);
          break;
        }

        case 'composition': {
          const step1 = getCompStep1(state);
          const step2 = getCompStep2(state);
          const model = buildCompositionModel(FLAG_VERTICES, FLAG_ORIGIN, step1, step2, 1e-6);

          const elems: FigureElement[] = [];

          elems.push(...buildFlagElems('comp-pre', model.preImage, false, 'geo-preimage'));
          elems.push(...buildFlagElems('comp-mid', model.intermediateImage, false, 'geo-ray-seg'));
          elems.push(...buildFlagElems('comp-fin', model.finalImage, false, 'geo-image'));

          if (state.compFirstPicker === 'reflect') {
            const axisLen = 100;
            const rad1 = (state.comp1AxisAngleDeg * Math.PI) / 180;
            const d1: Vec2 = { x: Math.cos(rad1), y: Math.sin(rad1) };
            const p1a = sub(state.comp1AxisPoint, scale(d1, axisLen));
            const p1b = add(state.comp1AxisPoint, scale(d1, axisLen));
            elems.push({
              kind: 'point',
              id: 'comp1-axis-p1',
              at: p1a,
              draggable: {},
              style: { className: 'geo-dilation-centre' },
            });
            elems.push({
              kind: 'point',
              id: 'comp1-axis-p2',
              at: p1b,
              draggable: {},
              style: { className: 'geo-dilation-centre' },
            });
            elems.push({
              kind: 'segment',
              id: 'comp1-axis-seg',
              from: 'comp1-axis-p1',
              to: 'comp1-axis-p2',
              style: { className: 'geo-intercept-seg' },
            });
          }

          if (state.compSecondPicker === 'reflect') {
            const axisLen = 100;
            const rad2 = (state.comp2AxisAngleDeg * Math.PI) / 180;
            const d2: Vec2 = { x: Math.cos(rad2), y: Math.sin(rad2) };
            const p2a = sub(state.comp2AxisPoint, scale(d2, axisLen));
            const p2b = add(state.comp2AxisPoint, scale(d2, axisLen));
            elems.push({
              kind: 'point',
              id: 'comp2-axis-p1',
              at: p2a,
              draggable: {},
              style: { className: 'geo-intercept-pt' },
            });
            elems.push({
              kind: 'point',
              id: 'comp2-axis-p2',
              at: p2b,
              draggable: {},
              style: { className: 'geo-intercept-pt' },
            });
            elems.push({
              kind: 'segment',
              id: 'comp2-axis-seg',
              from: 'comp2-axis-p1',
              to: 'comp2-axis-p2',
              style: { className: 'geo-ray-seg', dashed: true },
            });
          }

          figure.update(elems);
          marks.update([]);

          const rows: ReadoutRow[] = [
            {
              id: 'comp-step1',
              lhs: 'T₁',
              relation: '=',
              rhs: step1.label,
              emphasis: 'neutral',
            },
            {
              id: 'comp-step2',
              lhs: 'T₂',
              relation: '=',
              rhs: step2.label,
              emphasis: 'neutral',
            },
            {
              id: 'comp-equivalent',
              lhs: 'T₂∘T₁',
              relation: '=',
              rhs: model.equivalentLabel,
              emphasis: 'invariant',
            },
          ];
          readout.update(rows);
          break;
        }

        case 'tessellation': {
          const model = buildTessellationModel(state.tessTile, TESS_ORIGIN, 1, 1e-6);

          const elems: FigureElement[] = [];

          for (let i = 0; i < model.tileVertices.length; i++) {
            elems.push({
              kind: 'point',
              id: `tess-orig-pt-${i}`,
              at: model.tileVertices[i]!,
              style: { className: 'geo-dilation-centre' },
            });
          }
          elems.push({
            kind: 'polygon',
            id: 'tess-orig-poly',
            vertices: model.tileVertices.map((_, i) => `tess-orig-pt-${i}`),
            style: { className: 'geo-preimage-poly' },
          });

          for (let p = 0; p < model.placements.length; p++) {
            const placement = model.placements[p]!;
            for (let i = 0; i < placement.vertices.length; i++) {
              elems.push({
                kind: 'point',
                id: `tess-p${p}-pt-${i}`,
                at: placement.vertices[i]!,
                style: { className: 'geo-image-pt' },
              });
            }
            elems.push({
              kind: 'polygon',
              id: `tess-p${p}-poly`,
              vertices: placement.vertices.map((_, i) => `tess-p${p}-pt-${i}`),
              style: { className: 'geo-image-poly' },
            });
            if (placement.vertices.length > 0) {
              elems.push({
                kind: 'label',
                id: `tess-p${p}-lbl`,
                anchor: `tess-p${p}-pt-0`,
                offset: { x: 0, y: -8 },
                text: placement.transformClass.kind === 'translation' ? 'T' : 'T',
              });
            }
          }

          figure.update(elems);
          marks.update([]);

          const rows: ReadoutRow[] = [
            {
              id: 'tess-placements',
              lhs: 'Tiles placed',
              relation: '=',
              rhs: String(model.placements.length),
              emphasis: 'invariant',
            },
            {
              id: 'tess-tile',
              lhs: 'Tile',
              relation: '=',
              rhs: state.tessTile,
              emphasis: 'neutral',
            },
          ];
          readout.update(rows);
          break;
        }
      }
    },

    panel(state: TransformState, ctx): PanelRow<GeometryKey, TransformState>[] {
      const eMode = effectiveModeFor(state, ctx.advanced);
      const ePicker = effectivePickerFor(state, ctx.advanced);

      const rows: PanelRow<GeometryKey, TransformState>[] = [];

      // Mode dropdown
      rows.push({
        kind: 'dropdown',
        id: 'transform-mode',
        labelKey: 'panel.mode',
        options: [
          { value: 'transform', labelKey: 'transform.mode.transform' },
          { value: 'symmetry', labelKey: 'transform.mode.symmetry' },
          ...(ctx.advanced
            ? [
                { value: 'composition', labelKey: 'transform.mode.composition' },
                { value: 'tessellation', labelKey: 'transform.mode.tessellation' },
              ]
            : []),
        ] as { value: TransformMode; labelKey: GeometryKey }[],
        value: eMode,
        onChange(v: string) {
          const nextMode = v as TransformMode;
          const nextState = { ...state, mode: nextMode };
          if (nextMode === 'symmetry') {
            const symMirror = initSymMirrorState(state.symFigureId);
            nextState.symMirrorA = symMirror.symMirrorA;
            nextState.symMirrorB = symMirror.symMirrorB;
            nextState.symFoundAxes = symMirror.symFoundAxes;
            stopSymSpin();
          } else if (state.mode === 'symmetry') {
            stopSymSpin();
          }
          return nextState;
        },
      });

      // Transform picker dropdown (only in transform mode)
      if (eMode === 'transform') {
        rows.push({
          kind: 'dropdown',
          id: 'transform-picker',
          labelKey: 'transform.panel.transform',
          options: [
            { value: 'translate', labelKey: 'transform.picker.translate' },
            { value: 'rotate', labelKey: 'transform.picker.rotate' },
            { value: 'reflect', labelKey: 'transform.picker.reflect' },
            ...(ctx.advanced ? [{ value: 'dilate', labelKey: 'transform.picker.dilate' }] : []),
          ] as { value: TransformPicker; labelKey: GeometryKey }[],
          value: ePicker,
          onChange(v: string) {
            return { ...state, picker: v as TransformPicker };
          },
        });
      }

      // Figure picker dropdown (only in symmetry mode)
      if (eMode === 'symmetry') {
        rows.push({
          kind: 'dropdown',
          id: 'transform-figure',
          labelKey: 'transform.panel.figure',
          options: SYMMETRY_FIGURES.map((f) => ({
            value: f.id,
            labelKey: f.id as GeometryKey,
          })),
          value: state.symFigureId,
          onChange(v: string) {
            const symMirror = initSymMirrorState(v);
            stopSymSpin();
            return {
              ...state,
              symFigureId: v,
              symMirrorA: symMirror.symMirrorA,
              symMirrorB: symMirror.symMirrorB,
              symFoundAxes: symMirror.symFoundAxes,
            };
          },
        });
      }

      // Spin button (only in symmetry mode)
      if (eMode === 'symmetry') {
        rows.push({
          kind: 'button',
          labelKey: 'transform.symmetry.spin',
          onClick() {
            startSymSpin(state);
          },
        });
      }

      // Tile picker dropdown (only in tessellation mode)
      if (eMode === 'tessellation') {
        const tileOptions: TilePicker[] = ['square', 'hexagon', 'triangle', 'escher-square'];
        rows.push({
          kind: 'dropdown',
          id: 'transform-tile',
          labelKey: 'transform.panel.tile',
          options: tileOptions.map((t) => ({
            value: t,
            labelKey: `transform.tile.${t}` as GeometryKey,
          })),
          value: state.tessTile,
          onChange(v: string) {
            return { ...state, tessTile: v as TilePicker };
          },
        });
      }

      // Dilate k slider (only in advanced + transform mode + dilate picker)
      if (ctx.advanced && eMode === 'transform' && ePicker === 'dilate') {
        rows.push({
          kind: 'slider',
          id: 'transform-dilate-k',
          labelKey: 'transform.panel.dilate-k',
          min: -4,
          max: 4,
          step: 0.1,
          value: state.dilateK,
          format: (v) => v.toFixed(2),
          onChange(v: number) {
            return { ...state, dilateK: v };
          },
        });
      }

      // Advanced toggle (always last)
      rows.push({
        kind: 'advanced-toggle',
        labelKey: 'panel.advanced',
      });

      return rows;
    },

    actions(state, ctx): ActionBarSpec<GeometryKey, TransformState> {
      return {
        onReset() {
          stopSymSpin();
          return defaultState();
        },

        onRandomise(state: TransformState) {
          const eMode = effectiveModeFor(state, ctx.advanced);
          const ePicker = effectivePickerFor(state, ctx.advanced);

          if (eMode === 'transform') {
            const nextState = { ...state };
            if (ePicker === 'rotate') {
              nextState.rotateAngleDeg = snap15(ctx.prng() * 360 - 180);
              nextState.rotateCentre = {
                x: 100 + ctx.prng() * 600,
                y: 100 + ctx.prng() * 260,
              };
            } else if (ePicker === 'translate') {
              nextState.translateVec = {
                x: Math.round((ctx.prng() * 200 - 100) / 10) * 10,
                y: Math.round((ctx.prng() * 200 - 100) / 10) * 10,
              };
            } else if (ePicker === 'reflect') {
              nextState.reflectAxisAngleDeg = Math.round(ctx.prng() * 180);
              nextState.reflectAxisPoint = {
                x: 200 + ctx.prng() * 400,
                y: 100 + ctx.prng() * 260,
              };
            } else if (ePicker === 'dilate') {
              const rawK = 0.5 + ctx.prng() * 3.5;
              nextState.dilateK = Math.round(rawK * 10) / 10;
            }
            return nextState;
          } else if (eMode === 'symmetry') {
            const idx = Math.floor(ctx.prng() * SYMMETRY_FIGURES.length);
            const figId = SYMMETRY_FIGURES[idx]!.id;
            const symMirror = initSymMirrorState(figId);
            stopSymSpin();
            return {
              ...state,
              symFigureId: figId,
              symMirrorA: symMirror.symMirrorA,
              symMirrorB: symMirror.symMirrorB,
              symFoundAxes: symMirror.symFoundAxes,
            };
          }

          return state;
        },

        presets: TRANSFORM_PRESETS.filter(
          (p) =>
            p.mode === effectiveModeFor(state, ctx.advanced) && (ctx.advanced || !p.advancedOnly),
        ).map((p) => ({ id: p.id, labelKey: p.labelKey })),

        onPreset(state: TransformState, presetId: string) {
          return applyPresetLocal(state, presetId, ctx.advanced);
        },
      };
    },

    onDrag(state: TransformState, pointId: string, at: Vec2): TransformState {
      // Resolve mode/picker against the LIVE advanced flag so the drag branches
      // match exactly what `render` drew. The bespoke mount demoted `currentMode`
      // (and the dilate picker) to their foundation fallbacks when advanced was
      // off, so a visible transform handle stayed draggable even if the stored
      // mode/picker was an advanced-only one.
      const eMode = effectiveModeFor(state, controllerRef.advanced);
      const ePicker = effectivePickerFor(state, controllerRef.advanced);

      if (eMode === 'transform') {
        const nextState = { ...state };
        if (ePicker === 'translate') {
          if (pointId === 'tx-arrow-tip') {
            nextState.translateVec = sub(at, FLAG_ORIGIN);
          }
        } else if (ePicker === 'rotate') {
          if (pointId === 'rot-centre') {
            nextState.rotateCentre = at;
          } else if (pointId === 'rot-angle-handle') {
            const d = sub(at, state.rotateCentre);
            const rawAngle = Math.atan2(d.y, d.x) * (180 / Math.PI);
            nextState.rotateAngleDeg = snap15(rawAngle);
          }
        } else if (ePicker === 'reflect') {
          if (pointId === 'ref-axis-p1') {
            nextState.reflectAxisPoint = at;
          } else if (pointId === 'ref-axis-p2') {
            const d = sub(at, state.reflectAxisPoint);
            if (len(d) > 1) {
              nextState.reflectAxisAngleDeg = Math.atan2(d.y, d.x) * (180 / Math.PI);
            }
          }
        } else if (ePicker === 'dilate') {
          if (pointId === 'dil-centre') {
            nextState.dilateCentre = at;
          }
        }
        return nextState;
      } else if (eMode === 'composition') {
        const nextState = { ...state };
        if (pointId === 'comp1-axis-p1') {
          nextState.comp1AxisPoint = at;
        } else if (pointId === 'comp1-axis-p2') {
          const d = sub(at, state.comp1AxisPoint);
          if (len(d) > 1) nextState.comp1AxisAngleDeg = Math.atan2(d.y, d.x) * (180 / Math.PI);
        } else if (pointId === 'comp2-axis-p1') {
          nextState.comp2AxisPoint = at;
        } else if (pointId === 'comp2-axis-p2') {
          const d = sub(at, state.comp2AxisPoint);
          if (len(d) > 1) nextState.comp2AxisAngleDeg = Math.atan2(d.y, d.x) * (180 / Math.PI);
        }
        return nextState;
      } else if (eMode === 'symmetry') {
        const nextState = { ...state };
        const fig =
          SYMMETRY_FIGURES.find((f) => f.id === state.symFigureId) ?? SYMMETRY_FIGURES[0]!;
        const verts = fig.vertices;

        if (pointId === 'mirror-a') {
          nextState.symMirrorA = at;
        } else if (pointId === 'mirror-b') {
          nextState.symMirrorB = at;
        } else {
          return state;
        }

        // Test for axis match on the new mirror position
        const matchedIdx = matchSymmetryAxis(verts, nextState.symMirrorA, nextState.symMirrorB);
        if (matchedIdx !== null && !state.symFoundAxes.includes(matchedIdx)) {
          const snap = snapMirrorToAxis(verts, matchedIdx);
          nextState.symMirrorA = snap.symMirrorA;
          nextState.symMirrorB = snap.symMirrorB;
          nextState.symFoundAxes = [...state.symFoundAxes, matchedIdx];
          triggerSymmetryFlash();
        }

        return nextState;
      }

      return state;
    },

    applyShotState(state: TransformState, shotId: string) {
      if (shotId === 'transformations') {
        // The screenshot shot requires composition mode (advanced only).
        // Since advanced is resolved via ?adv=1 in the URL and the harness applies it
        // before applyShotState runs, composition will display when advanced is true.
        return { ...state, mode: 'composition' };
      }
      return state;
    },
  };
}

function getCompStep1(state: TransformState): CompositionStep {
  if (state.compFirstPicker === 'reflect') {
    const dir1 = (() => {
      const rad = (state.comp1AxisAngleDeg * Math.PI) / 180;
      return { x: Math.cos(rad), y: Math.sin(rad) };
    })();
    return {
      picker: 'reflect',
      affine: reflection(state.comp1AxisPoint, dir1),
      label: `Reflect ${Math.round(state.comp1AxisAngleDeg)}°`,
    };
  }
  return {
    picker: 'translate',
    affine: translation({ x: 60, y: 0 }),
    label: 'Translate (60, 0)',
  };
}

function getCompStep2(state: TransformState): CompositionStep {
  if (state.compSecondPicker === 'reflect') {
    const dir2 = (() => {
      const rad = (state.comp2AxisAngleDeg * Math.PI) / 180;
      return { x: Math.cos(rad), y: Math.sin(rad) };
    })();
    return {
      picker: 'reflect',
      affine: reflection(state.comp2AxisPoint, dir2),
      label: `Reflect ${Math.round(state.comp2AxisAngleDeg)}°`,
    };
  }
  return {
    picker: 'rotate',
    affine: rotation({ x: 400, y: 230 }, 45),
    label: 'Rotate 45°',
  };
}

function applyPresetLocal(
  state: TransformState,
  presetId: string,
  advanced: boolean,
): TransformState {
  const p = TRANSFORM_PRESETS.find((x) => x.id === presetId);
  if (!p || (p.advancedOnly && !advanced)) return state;

  const nextState = { ...state, mode: p.mode };

  if (p.mode === 'symmetry') {
    const symMirror = initSymMirrorState(state.symFigureId);
    nextState.symMirrorA = symMirror.symMirrorA;
    nextState.symMirrorB = symMirror.symMirrorB;
    nextState.symFoundAxes = symMirror.symFoundAxes;
  }

  if (p.picker) nextState.picker = p.picker;
  if (p.angleDeg !== undefined) nextState.rotateAngleDeg = p.angleDeg;
  if (p.mirrorAngleDeg !== undefined) {
    nextState.reflectAxisAngleDeg = p.mirrorAngleDeg;
    nextState.comp1AxisAngleDeg = p.mirrorAngleDeg;
    nextState.comp2AxisAngleDeg = p.mirrorAngleDeg;
  }
  if (p.tile) nextState.tessTile = p.tile;
  if (p.firstPicker) nextState.compFirstPicker = p.firstPicker;
  if (p.secondPicker) nextState.compSecondPicker = p.secondPicker;

  return nextState;
}

export const experiment = defineExperiment(buildSpec());
